import type {
  ColumnConfigSnapshot,
  MapCardFieldKey,
  SavedViewCardFields,
  SavedViewFilter,
  SavedViewLayout,
} from '@/stores/persistentTableStore';
import type { SortDirection } from '@/types/tableTypes';
import { columnDisplayOrdinal, compareColumnDisplayOrder, isColumnShown } from '@/utils/columnConfigSnapshot';
import { entityColumnUniverse, defaultWidthFor } from '@/composables/entityColumnUniverse';
import { cardDefaultColumnIdsFor } from '@/composables/cardDefaultColumns';

/**
 * The stored shape of a saved view (SAU-310).
 *
 * The live `ColumnConfigSnapshot` is five maps keyed by EVERY column of the
 * entity. On Location Zone Player that is 79 keys each, and almost all of it is
 * defaults — `columnPin` is 79 entries all `false`, `columnSize` 79 entries of
 * which 77 are the default 150. Pruning to the visible set plus the non-default
 * sizes, pins and sorts takes a real view from 8,904 bytes of JSON to about 805.
 *
 * `sorts` is an ARRAY of pairs, never an object. MySQL re-sorts JSON object keys
 * (by length, then bytewise), and `columnSort`'s insertion order encodes
 * multi-sort priority — see `columnConfigSnapshot.ts`, which walks its keys in
 * sequence precisely so two layouts with the same sort fields in a different
 * priority compare as unequal. An object here would be corrupted by the database,
 * silently, and the view that prompted this work has two sort columns.
 */
export interface SavedViewDefinitionV2 {
  v: 2;
  /** Ordered visible column ids. Anything not listed is hidden. */
  columns: string[];
  /** Pinned column ids. Pinned columns display before unpinned ones. */
  pinned: string[];
  /** [field, direction] pairs, most significant first. */
  sorts: [string, SortDirection][];
  /** Only widths that differ from the entity's default for that column. */
  sizes: Record<string, number>;
  filter: SavedViewFilter;
  layout?: SavedViewLayout;
  /**
   * Ordered card-visible column ids. OPTIONAL. It is absent on a view saved
   * before card capture existed, and on a view whose author never customised
   * their cards. Both mean "the record type's default card fields"
   * (cardDefaultColumnIdsFor). Opening such a view puts the default card
   * fields back, and the dirty check compares the live cards against them.
   */
  cards?: string[];
  /** Which address lines a map pin's card shows. Optional, same rule as `cards`. */
  mapFields?: Record<MapCardFieldKey, boolean>;
  /**
   * The field the list is grouped by, or null for no grouping. Written on
   * every save. It is absent on a view saved before grouping was stored, and
   * absent means no grouping: the view opens ungrouped, and it reads as
   * unchanged while the list is ungrouped.
   */
  groupBy?: string | null;
}

/** The version this client writes. Reading is forward-tolerant; writing is not. */
export const SAVED_VIEW_DEFINITION_VERSION = 2;

/**
 * Live column state -> stored definition. Reads the entity's column universe so
 * columns the snapshot never mentioned still take their schema position, the way
 * `usePersistentTable` falls back to `index + 1`.
 */
export function pruneToDefinition(
  entityId: string,
  columnConfig: ColumnConfigSnapshot,
  filter: SavedViewFilter,
  layout?: SavedViewLayout | undefined,
  cardFields?: SavedViewCardFields | undefined,
  groupBy: string | null = null,
): SavedViewDefinitionV2 {
  const universe = entityColumnUniverse(entityId);
  const knownFields = new Set(universe.map((column) => column.field));

  const ordered = universe
    .map((column, index) => ({
      id: column.id,
      field: column.field,
      isPinned: columnConfig.columnPin?.[column.id] === true,
      // The table's own rule, so a column hidden by default stays hidden in a view
      // that has no entry for it.
      isVisible: isColumnShown(column, columnConfig.columnHidden),
      order: columnConfig.columnOrder?.[column.id] ?? index + 1,
    }))
    .sort((a, b) => compareColumnDisplayOrder(a.order, b.order));

  const sizes: Record<string, number> = {};
  for (const column of universe) {
    // A fixed-width column renders at `column.fixedWidth` whatever the stored
    // width says — `usePersistentTable` resolves `col.fixedWidth ?? store...` —
    // and the header refuses to resize it. So a width for one is never a user
    // choice; it is whatever Column Settings' Save echoed back out of the
    // rendered table. Storing it made a no-op Save read as an edit: Play is 60
    // and History is 80, `defaultWidthFor` answers 150 for both, so both landed
    // in `sizes` and lit "Update {name}" on a table nobody had touched.
    if (typeof column.fixedWidth === 'number') {
      continue;
    }
    const size = columnConfig.columnSize?.[column.id];
    if (typeof size === 'number' && size !== defaultWidthFor(entityId, column.id)) {
      sizes[column.id] = size;
    }
  }

  // Card visibility is stored CANONICALLY — universe order, ids only — the way
  // `columns` is, never as the raw map. The inspector writes an explicit entry
  // for every column each time it saves, so the live map is ~79 mostly-false
  // keys on Location Zone Player, and two views showing identical fields would
  // compare unequal because one carried an explicit `false` the other omitted.
  //
  // Only captured once the user has actually customised. An empty live map
  // means "showing this page's defaults", and writing `[]` for that would tell
  // every future reader to show NO card fields.
  //
  // A column with no entry in the live map still shows when it is one of the
  // record type's default card fields, so it is listed too. Ticking one field
  // writes an entry for that field only. On Location Zone Player the cards then
  // show "Currently playing" and the ticked field, and the saved list must say
  // the same or reopening the view would drop "Currently playing".
  const liveCardVisible = cardFields?.cardColumnVisible ?? {};
  const cards = Object.keys(liveCardVisible).length > 0
    ? cardIdsShownBy(entityId, liveCardVisible)
    : undefined;

  return {
    v: SAVED_VIEW_DEFINITION_VERSION,
    columns: ordered.filter((column) => column.isVisible).map((column) => column.id),
    // Visible pinned columns first, hidden pinned ones after — the order
    // expandDefinition will hand back, since it queues every hidden column
    // behind the visible ones. Listing them in raw ordinal order put a hidden
    // pinned column that happened to sit first AHEAD of a visible one, and the
    // positional comparison then read the freshly loaded view as modified.
    //
    // The hidden half comes from the UNIVERSE, not from `ordered`: expand hands
    // hidden columns their ordinals by walking the universe, so two hidden pinned
    // columns dragged into the reverse of universe order would otherwise come
    // back swapped and the view would read dirty on every load.
    pinned: [
      ...ordered.filter((column) => column.isPinned && column.isVisible).map((column) => column.id),
      ...universe
        .filter(
          (column) =>
            columnConfig.columnPin?.[column.id] === true && !isColumnShown(column, columnConfig.columnHidden),
        )
        .map((column) => column.id),
    ],
    // Object.entries preserves insertion order, which IS the sort priority.
    //
    // Filtered on the SAME two conditions expandDefinition applies on the way
    // back in. It kept an unknown field or a bad direction, expand dropped it,
    // and the view then compared unequal to its own stored definition the
    // moment it finished loading — the table showed exactly the right thing
    // and the amber dirty dot never went out. The direction half matters as
    // much as the field half: the types say a direction is 'asc' | 'desc', but
    // this arrives from a localStorage blob that is not bound by the types.
    sorts: (Object.entries(columnConfig.columnSort ?? {}) as [string, SortDirection][]).filter(
      ([field, direction]) =>
        knownFields.has(field) && (direction === 'asc' || direction === 'desc'),
    ),
    sizes,
    filter,
    layout,
    cards,
    mapFields: cardFields?.mapCardFieldVisibility,
    // Checked the way expandGroupBy checks it on the way back in. A grouping
    // on a field the universe does not know would be dropped when the view is
    // opened, and the view would then read as changed against itself.
    groupBy: groupBy !== null && knownFields.has(groupBy) ? groupBy : null,
  };
}

/**
 * Stored grouping -> the live `groupBy`. Null for no grouping.
 *
 * A view saved before grouping was stored has no `groupBy`, and opens with no
 * grouping rather than keeping whatever grouping the list had. A grouping on a
 * field the record type no longer has is dropped the same way, so the rest of
 * the view still opens.
 */
export function expandGroupBy(entityId: string, definition: SavedViewDefinitionV2): string | null {
  const groupBy = definition.groupBy;
  if (typeof groupBy !== 'string') {
    return null;
  }
  const knownFields = new Set(entityColumnUniverse(entityId).map((column) => column.field));
  return knownFields.has(groupBy) ? groupBy : null;
}

/**
 * Stored card fields -> live card state.
 *
 * A definition with no card list gives an EMPTY card map, which is the store's
 * "show the record type's default card fields" state. It used to give nothing
 * at all, which left the reader's own card fields on screen. That disagreed
 * with the dirty check, which compares a missing list against the defaults: a
 * reader with customised cards opened the view, saw the dot at once, and
 * "Discard changes" could not clear it.
 *
 * `mapCardFieldVisibility` is still left out when the definition has no
 * `mapFields`, so the reader's map pins stay as they are.
 */
export function expandCardFields(
  entityId: string,
  definition: SavedViewDefinitionV2,
): SavedViewCardFields {
  // The two halves are INDEPENDENT. `mapFields` is written on every save while
  // `cards` is written only once the author has customised their cards, so a
  // definition very commonly carries the first without the second. Treating
  // the pair as one unit once turned that into `cards ?? []` — an explicit
  // false for every column in the universe — and loading any such view blanked
  // the reader's cards, defaults included.
  //
  // A missing list is therefore `{}`, never a map of falses: `{}` shows the
  // defaults, and a map of falses shows nothing.
  const result: SavedViewCardFields = { cardColumnVisible: {} };

  // Array.isArray, not `!== undefined`: the server validates no shape for
  // `cards`, so a crafted row can hold `cards: null`, and `.filter` on that
  // would throw inside applyView after the filter and columns were written.
  if (Array.isArray(definition.cards)) {
    const universe = entityColumnUniverse(entityId);
    const knownColumnIds = new Set(universe.map((column) => column.id));
    const visible = new Set(definition.cards.filter((id) => knownColumnIds.has(id)));

    // An entry for EVERY column, not just the visible ones — the same reason
    // expandDefinition writes every column. A partial map lets the previous
    // view's card fields show through for anything this one does not mention.
    const cardColumnVisible: Record<string, boolean> = {};
    for (const column of universe) {
      cardColumnVisible[column.id] = visible.has(column.id);
    }
    result.cardColumnVisible = cardColumnVisible;
  }

  if (definition.mapFields) {
    // The store normalises this on the way in, which drops unknown keys and
    // non-booleans — so a hand-edited share link cannot write junk here.
    result.mapCardFieldVisibility = definition.mapFields as Record<MapCardFieldKey, boolean>;
  }

  return result;
}

/**
 * Stored definition -> live column state.
 *
 * Applying a snapshot replaces each map WHOLESALE, so every column in the
 * universe gets an entry here — a partial map would leave the previous view's
 * state showing through for any column this one does not mention.
 */
export function expandDefinition(
  entityId: string,
  definition: SavedViewDefinitionV2,
): ColumnConfigSnapshot {
  const universe = entityColumnUniverse(entityId);
  const knownColumnIds = new Set(universe.map((column) => column.id));
  const knownFields = new Set(universe.map((column) => column.field));

  // Column ids drift as the schema changes, so a view from six months ago may
  // name columns that no longer exist. Skipped rather than thrown on — the rest
  // of the view must still load.
  const visible = (definition.columns ?? []).filter((id) => knownColumnIds.has(id));
  const pinned = new Set((definition.pinned ?? []).filter((id) => knownColumnIds.has(id)));

  // Every column that survived renaming is hidden; the visible ones then get
  // ordinals in the order the definition listed them.
  const columnHidden: Record<string, boolean> = {};
  const columnPin: Record<string, boolean> = {};
  const columnSize: Record<string, number> = {};
  const columnOrder: Record<string, number> = {};

  for (const column of universe) {
    columnHidden[column.id] = true;
    columnPin[column.id] = pinned.has(column.id);
    columnSize[column.id] = definition.sizes?.[column.id] ?? defaultWidthFor(entityId, column.id);
  }

  visible.forEach((columnId, index) => {
    columnHidden[columnId] = false;
    columnOrder[columnId] = columnDisplayOrdinal(index, pinned.has(columnId));
  });

  // Hidden columns still need an ordinal so unhiding one later puts it somewhere
  // sensible instead of at position 0. They queue up after the visible ones.
  let nextHiddenIndex = visible.length;
  for (const column of universe) {
    if (columnOrder[column.id] === undefined) {
      columnOrder[column.id] = columnDisplayOrdinal(nextHiddenIndex, pinned.has(column.id));
      nextHiddenIndex += 1;
    }
  }

  // Rebuilt in array order, because insertion order is the sort priority.
  const columnSort: Record<string, SortDirection> = {};
  for (const entry of definition.sorts ?? []) {
    if (!Array.isArray(entry) || entry.length !== 2) {
      continue;
    }
    const [field, direction] = entry;
    if (knownFields.has(field) && (direction === 'asc' || direction === 'desc')) {
      columnSort[field] = direction;
    }
  }

  return { columnOrder, columnSort, columnHidden, columnPin, columnSize };
}

/**
 * Whether a value read off the wire is a definition this client can apply.
 * Anything else is a link from a newer deploy, and the caller shows the
 * broken-link state rather than guessing.
 */
/** Absent, or a real array. Optional keys are legitimately missing on old views. */
function isAbsentOrList(value: unknown): boolean {
  return value === undefined || value === null || Array.isArray(value);
}

/**
 * Absent, or a map — with an EMPTY ARRAY counting as an empty map.
 *
 * That last clause is not a nicety, it is the round trip. PHP decodes a JSON
 * object into an array, so an empty `{}` sent by this client is stored and
 * re-encoded as `[]`: send `sizes: {}` and the server hands back `sizes: []`.
 * Rejecting arrays outright therefore condemned every view with no custom
 * column widths — which is most of them — as unsupported. Two e2e specs caught
 * it. A NON-empty array is still wrong, and is still refused.
 */
function isAbsentOrMap(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return typeof value === 'object';
}

export function isSupportedDefinition(value: unknown): value is SavedViewDefinitionV2 {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const candidate = value as Record<string, unknown>;

  if (candidate.v !== SAVED_VIEW_DEFINITION_VERSION) {
    return false;
  }

  // THE SHAPES EXPANSION ACTUALLY USES, not just the two it used to check.
  //
  // The server stores a definition as opaque JSON and validates only its size
  // and entry counts — never the type of a value. Probed against the running
  // API: `pinned` is accepted as an object, a string or a number, and `sorts`
  // as an object, and each comes back to the client exactly as it was sent.
  //
  // The guard then declared them supported, and expandDefinition ran
  // `pinned.filter(...)` and `for (const entry of sorts)` on them. A share link
  // carrying one threw partway through applying the view — after the import had
  // already created the recipient's copy, so they were left on a dead page
  // owning a view they never saw.
  //
  // `pinned: {}` is deliberately NOT among the shapes this rejects, because it
  // never reaches here: PHP folds an empty JSON object to an empty array, so it
  // round-trips as `[]`. It is the non-empty shapes that survive the trip.
  // The filter must actually carry a rule tree. `typeof {} === 'object'` let a
  // bare `filter: {}` through, and applyDefinitionToScope then wrote it straight
  // into a scope. Reading the ROUTED scope repairs it — normalizeTableState
  // rebuilds the root — but an import writes to the VIEW's scope rather than the
  // one you are standing on, and the repair never reaches it. The dirty check
  // suffers either way: it compares a repaired live root scope against
  // `undefined` and reports the view as modified from the moment it loads.
  //
  // `ruleScopes` is allowed to be an array, because an empty one round-trips
  // through PHP as `[]` — the same fold that turns `sizes: {}` into `[]`.
  const filter = candidate.filter as Record<string, unknown> | null;
  const hasRuleTree = filter !== null
    && typeof filter === 'object'
    && !Array.isArray(filter)
    && filter.ruleScopes !== null
    && typeof filter.ruleScopes === 'object';

  return Array.isArray(candidate.columns)
    && hasRuleTree
    && isAbsentOrList(candidate.pinned)
    && isAbsentOrList(candidate.sorts)
    && isAbsentOrList(candidate.cards)
    && isAbsentOrMap(candidate.sizes)
    && isAbsentOrMap(candidate.mapFields)
    && (candidate.groupBy === undefined || candidate.groupBy === null || typeof candidate.groupBy === 'string');
}

/**
 * A stored definition with every id the current universe no longer knows removed.
 *
 * The reader and the writer disagreed without this. Expanding a view DROPS ids the
 * universe does not know — deliberately, so a view from six months ago still loads
 * — while the live definition is rebuilt FROM the universe and can never contain
 * one. Comparing the two therefore never matched again: the amber "modified" dot
 * was on from the moment the view opened, "Update {name}" never went quiet, and the
 * only way to clear it was to overwrite the view.
 *
 * Any backend field rename or removal gets you there, and `src/ts` is regenerated
 * from the backend, so that is routine — as is opening a share link from someone
 * whose deploy is newer than yours.
 *
 * `sorts` already had this treatment, applied on the write side in
 * pruneToDefinition; this is the same idea for the id-bearing keys, applied to the
 * stored side at the moment of comparison so that nothing is rewritten on disk.
 *
 * The grouping gets the same treatment: a grouping on a field the universe does
 * not know reads as no grouping, which is how expandGroupBy opens it.
 */
export function definitionWithoutUnknownColumns(
  entityId: string,
  definition: SavedViewDefinitionV2,
): SavedViewDefinitionV2 {
  const knownColumnIds = new Set(entityColumnUniverse(entityId).map((column) => column.id));
  const keepKnown = (ids: string[] | undefined): string[] | undefined =>
    ids === undefined ? undefined : ids.filter((id) => knownColumnIds.has(id));

  const sizes = Object.fromEntries(
    Object.entries(definition.sizes ?? {}).filter(([id]) => knownColumnIds.has(id)),
  );

  return {
    ...definition,
    columns: keepKnown(definition.columns) ?? [],
    pinned: keepKnown(definition.pinned) ?? [],
    cards: keepKnown(definition.cards),
    sizes,
    groupBy: expandGroupBy(entityId, definition),
  };
}

/**
 * The card fields a live card map shows, in universe order. A column shows when
 * its entry is true, or when it has no entry and is one of the record type's
 * default card fields. The same rule `usePersistentTable.cardVisibleColumns`
 * uses to draw the cards.
 */
function cardIdsShownBy(entityId: string, cardColumnVisible: Record<string, boolean>): string[] {
  const defaults = new Set(cardDefaultColumnIdsFor(entityId));
  return entityColumnUniverse(entityId)
    .filter((column) => cardColumnVisible[column.id] ?? defaults.has(column.id))
    .map((column) => column.id);
}

/** A definition with its missing card list read as the default card fields. */
function withDefaultCardsWhenMissing(
  entityId: string,
  definition: SavedViewDefinitionV2,
): SavedViewDefinitionV2 {
  if (Array.isArray(definition.cards)) {
    return definition;
  }
  return { ...definition, cards: cardIdsShownBy(entityId, {}) };
}

/**
 * Whether the live table differs from a stored view, EXCLUDING the filter.
 * This is what the saved-view dot and the Save button read.
 *
 * Two things are settled here before the comparison, and both need the record
 * type:
 *
 * - The stored side loses the column ids the current universe no longer knows.
 *   See definitionWithoutUnknownColumns.
 * - A missing card list, on either side, is read as the record type's default
 *   card fields. Before, a missing list compared equal to ANY list. A view saved
 *   while the cards showed their defaults therefore never noticed a card change:
 *   ticking a field showed no dot and no Save button.
 */
export function definitionDiffersIgnoringFilter(
  entityId: string,
  liveDefinition: SavedViewDefinitionV2,
  storedDefinition: SavedViewDefinitionV2,
): boolean {
  return !definitionsEqualIgnoringFilter(
    withDefaultCardsWhenMissing(entityId, liveDefinition),
    withDefaultCardsWhenMissing(entityId, definitionWithoutUnknownColumns(entityId, storedDefinition)),
  );
}

/**
 * Deep-equal two pruned definitions, EXCLUDING the filter.
 *
 * The filter is compared separately by `scopesEqual`, which understands the
 * rule tree's own equality (ids and empty groups are not meaningful there).
 * Everything else in a definition is either an ordered list or a flat map of
 * primitives, so a structural walk is enough.
 *
 * This exists because dirty-detection has to compare PRUNED definitions rather
 * than raw column maps. Live state carries incidental entries a canonical
 * expansion never reproduces — a width the user never changed, a pin entry set
 * to false — so comparing the maps makes every freshly loaded view read as
 * permanently dirty and the "Update {name}" button never goes quiet.
 */
export function definitionsEqualIgnoringFilter(
  a: SavedViewDefinitionV2,
  b: SavedViewDefinitionV2,
): boolean {
  if (a.v !== b.v) {
    return false;
  }
  if (!orderedListsEqual(a.columns, b.columns) || !orderedListsEqual(a.pinned, b.pinned)) {
    return false;
  }
  // Sort order is priority, so this comparison is ordered too — the whole
  // reason sorts are an array and not an object. Absent counts as empty: the
  // server rule never requires `sorts`, isSupportedDefinition does not check
  // it, and this runs inside the isModified computed, where a bare `.length`
  // on undefined took the whole toolbar control down for that scope.
  const aSorts = a.sorts ?? [];
  const bSorts = b.sorts ?? [];
  if (aSorts.length !== bSorts.length) {
    return false;
  }
  for (let index = 0; index < aSorts.length; index++) {
    const left = aSorts[index];
    const right = bSorts[index];
    // Entries are checked the way expandDefinition checks them: the server rule
    // counts `sorts` entries and never looks inside one.
    if (!Array.isArray(left) || !Array.isArray(right) || left[0] !== right[0] || left[1] !== right[1]) {
      return false;
    }
  }
  if (!sizeMapsEqual(a.sizes, b.sizes)) {
    return false;
  }
  if (!optionalListsEqual(a.cards, b.cards)) {
    return false;
  }
  if (!optionalFlagMapsEqual(a.mapFields, b.mapFields)) {
    return false;
  }
  // A missing grouping is no grouping, and is compared that way. It is not
  // given the absent-means-equal carve-out the card fields once had: that
  // carve-out is what hid every card change on a view saved with the defaults.
  if ((a.groupBy ?? null) !== (b.groupBy ?? null)) {
    return false;
  }
  return layoutsEqual(a.layout, b.layout);
}

/**
 * Ordered lists where ABSENT counts as equal to anything — deliberately not
 * `orderedListsEqual`, which coerces undefined to `[]`. A view saved before
 * card capture existed has no `cards`, and comparing that as an empty list
 * against a live table that does have them would mark every one of those views
 * permanently dirty. This is the carve-out `layoutsEqual` already makes.
 *
 * For `cards`, the saved-view dot never reaches this carve-out any more:
 * definitionDiffersIgnoringFilter fills a missing card list with the record
 * type's default card fields before it compares.
 */
function optionalListsEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  if (a === undefined || b === undefined) {
    return true;
  }
  return orderedListsEqual(a, b);
}

/**
 * Same absent-means-equal rule, for a flat map of booleans. Compared BY KEY,
 * never by serialising: MySQL re-sorts JSON object keys (by length, then
 * bytewise), so a stringify comparison would report every view as dirty after
 * its first round trip through the database — with the cause nowhere near the
 * symptom. This is the same hazard that made `sorts` an array of pairs.
 */
function optionalFlagMapsEqual(
  a: Record<string, boolean> | undefined,
  b: Record<string, boolean> | undefined,
): boolean {
  if (a === undefined || b === undefined) {
    return true;
  }
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) {
    return false;
  }
  return keys.every((key) => a[key] === b[key]);
}

function orderedListsEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
}

function sizeMapsEqual(
  a: Record<string, number> | undefined,
  b: Record<string, number> | undefined,
): boolean {
  const left = a ?? {};
  const right = b ?? {};
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) {
    return false;
  }
  return leftKeys.every((key) => left[key] === right[key]);
}

/**
 * Absent layouts count as equal to each other. A view saved before layout
 * capture existed has none, and must not read as permanently dirty against a
 * live table that does have one — the same carve-out the old snapshot
 * comparison made.
 */
function layoutsEqual(a: SavedViewLayout | undefined, b: SavedViewLayout | undefined): boolean {
  if (!a || !b) {
    return true;
  }
  // A view saved while the row-click setting existed may hold `rowClickAction`.
  // It is ignored, so it never makes the view read as changed.
  return a.tableLayout === b.tableLayout && a.searchViewMode === b.searchViewMode;
}
